import React, { useState, useRef } from "react";
import { SwipeableDrawer, Box } from "@mui/material";
import Util from "../../Util";
import Close from "../../assets/Icon/close.png";
import CustomFileUpload from "../../screens/Dashbord/CustomFileUpload";
import Invalid from "../../assets/Icon/invalid.svg";
import ApiCalling from "../../network/ApiCalling";
import ExitAlert from "../ExitAlert";
import { is } from "date-fns/locale";
import Loader from "../common/Loader";

const AddDocuments = ({ show, onClose, onSave }) => {
  const getLanguage = Util.getSelectedLanguage();
  const [alertModal, setIsAlertModal] = useState(false);
  const [isStateUpdated, setIsStateUpdated] = useState(false);
  const [newDoc, setNewDoc] = useState({ title: "", file: null });
  const [fileURL, setFileURL] = useState("");
  const [fileError, setFileError] = useState({ title: "", file: "" });
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setNewDoc({ title: "", file: null });
    setFileURL("");
    setFileError({ title: "", file: "" });
    setIsStateUpdated(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDoc({ ...newDoc, [name]: value });
    setIsStateUpdated(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    // Reset state
    setNewDoc((prev) => ({ ...prev, file: null }));
    setFileError({ file: "" });

    // Null check
    if (!file) {
      setFileError({ file: "No file selected." });
      return;
    }

    // File size limit (25MB)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError({ file: "File size must be 500MB or less." });
      return;
    }

    const validExtensions =
      /\.(jpg|jpeg|png|gif|webp|heic|heif|mp4|mkv|webm|mov|avi|3gp|pdf)$/i;

    const isValidExtension = validExtensions.test(file.name);
    // console.log("isValidExtension", isValidExtension);
    if (!isValidExtension) {
      setFileError({
        file: "Unsupported file type. Please upload a valid image, video, or PDF file.",
      });
      return;
    }

    // All validations passed
    handleDocumentChange(file);
  };

  const handleDocumentDragOver = (e) => {
    e.preventDefault();
  };

  const handleDocumentDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleDocumentChange(file);
  };

  const handleDocumentChange = (file) => {
    if (file) {
      const maxSize = 500 * 1024 * 1024;
      if (file.size <= maxSize) {
        setNewDoc({ ...newDoc, file });
        setFileURL(URL.createObjectURL(file));
        setFileError((prev) => ({ ...prev, file: "" }));
        setIsStateUpdated(true);
      } else {
        setFileError((prev) => ({
          ...prev,
          file: "File size exceeds the limit of 25MB",
        }));
      }
    }
  };

  const handleAddDocument = async () => {
    setLoading(true);
    let hasError = false;

    if (!newDoc.title) {
      setFileError((prev) => ({
        ...prev,
        title: "Document title is required",
      }));
      hasError = true;
    } else {
      setFileError((prev) => ({ ...prev, title: "" }));
    }

    if (!newDoc.file) {
      setFileError((prev) => ({
        ...prev,
        file: "Document file is required",
      }));
      hasError = true;
    } else {
      setFileError((prev) => ({ ...prev, file: "" }));
    }

    if (hasError) return;

    const newDocument = {
      title: newDoc.title,
      file: newDoc.file,
    };
    const formData = new FormData();
    formData.append("title", newDocument.title);

    const file = newDocument.file;
    let progress = 0;
    try {
      const uploadSummary = await Util.uploadFilesToAzure(
        [file],
        "files",
        null,
        {
          compress: true,
          onProgress: (result, index, total, error, progressValue, file) => {
            if (error) {
              console.error(`Upload failed for file ${index + 1}/${total}`);
            }
            progress = progressValue;
            console.log(`File upload progress: ${progress}%`);
          },
          onComplete: (summary) => {
            console.log("Upload completed:", summary);
          },
        }
      );
      const uploadedFile = uploadSummary.uploads[0];
      formData.append("files", uploadedFile?.blobName);
    } catch (err) {
      console.error("Upload error:", err);
    }

    console.log("newDocument", newDocument);
    ApiCalling.apiCallBodyDataPost("school/upload-document", formData)
      .then((response) => {
        console.log("Document uploaded successfully:", response);
        onSave("Added");
        resetForm();
      })
      .catch((error) => {
        console.error("Error uploading document:", error);
        onSave(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCloseDrawer = () => {
    console.log("isStateUpdated", isStateUpdated);
    if (isStateUpdated) {
      setIsAlertModal(true);
    } else {
      resetForm();
      onClose();
    }
  };

  return (
    <SwipeableDrawer
      anchor={getLanguage === "ar" ? "left" : "right"}
      open={show}
      onClose={handleCloseDrawer}
      onOpen={() => {}}
    >
      <div
        className="box-area"
        style={{
          position: "relative",
          width: "100%",
          height: "calc(100vh - 64px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Box sx={{ width: "656px", padding: "20px" }} role="presentation">
          <div className="d-flex align-items-center justify-content-between">
            <div className="drawer-header">Add Documents</div>
            <button className="hidden-btn" onClick={handleCloseDrawer}>
              <img
                src={Close}
                alt="Close"
                className="svg-icon"
                height={20}
                width={20}
              />
            </button>
          </div>

          {!loading ? (
            <>
              {/* Title Input */}
              <div className="mb-4">
                <div className="mb-2">
                  <span className="basic-text required">Title</span>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={newDoc.title}
                    onChange={handleInputChange}
                    required
                  />
                  {fileError.title && (
                    <span className="text-danger invalid-error">
                      <img
                        className="fa fa-exclamation-circle mr-2 invalid-svg"
                        src={Invalid}
                        alt="!"
                      />
                      {fileError.title}
                    </span>
                  )}
                </div>
              </div>

              {/* File Upload */}
              <div className="mt-4">
                <div className="input-filed">
                  <span className="basic-text required">Upload Document</span>
                  <CustomFileUpload
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    fileName={newDoc.file?.name || ""}
                    showFile={fileURL}
                    onDragOver={handleDocumentDragOver}
                    onDrop={handleDocumentDrop}
                    alertMessage={
                      newDoc.file
                        ? "(In case you select a new file, it will replace the old one)"
                        : ""
                    }
                    acceptedDocument="Image, Video, PDF (max file 25MB)"
                    accept="image/*,video/*,application/pdf"
                    className={`${
                      fileError?.file ? "custom-file-error" : "custom-file"
                    }`}
                  />
                  {fileError.file && (
                    <span className="text-danger invalid-error">
                      <img
                        className="fa fa-exclamation-circle mr-2 invalid-svg"
                        src={Invalid}
                        alt="!"
                      />
                      {fileError.file}
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center my-5">
              <Loader />
            </div>
          )}

          {/* Footer Actions */}
          <div
            className="border-top"
            style={{
              position: "fixed",
              width: "656px",
              bottom: "0px",
              right: getLanguage !== "ar" ? "0px" : undefined,
              left: getLanguage === "ar" ? "0px" : undefined,
              background: "#ffffff",
              padding: "10px",
            }}
          >
            <button
              className="btn secondary-lg-btn"
              onClick={handleCloseDrawer}
            >
              Cancel
            </button>
            <button
              style={{ float: "right" }}
              className="btn primary-lg-btn"
              onClick={handleAddDocument}
              disabled={loading}
            >
              Save
            </button>
          </div>
        </Box>
      </div>

      {/* Exit Alert */}
      <ExitAlert
        visible={alertModal}
        onClose={() => setIsAlertModal(false)}
        onOk={() => {
          setIsAlertModal(false);
          onClose();
          resetForm();
        }}
      />
    </SwipeableDrawer>
  );
};

export default AddDocuments;
